import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, PackageCheck } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { OrderRecord, OrderStatus } from "@/types";
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge";

/**
 * The deal's sales order. Once the deal is Won (or at Order Confirmed with the PO recorded) it offers to make the
 * order from the accepted quotation and the buyer's PO; afterwards it links to the order and shows its progress.
 */
export function DealOrderCard({ dealId, dealStatus, stageKey, onChanged }: { dealId: string; dealStatus: string; stageKey?: string | null; onChanged: () => void }) {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [po, setPo] = useState({ po_number: "", po_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const canRead = hasPermission("order.read");
  const canCreate = hasPermission("order.create");

  const load = useCallback(async () => {
    if (!canRead) return;
    try {
      setOrders(unwrapPage<OrderRecord>(await api.get(`/api/SalesOrder/List?deal_id=${dealId}&page_size=20`)).rows);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [dealId, canRead]);

  useEffect(() => {
    load();
  }, [load]);

  const live = orders.filter((o) => o.order_status !== "CANCELLED");
  // Only a won deal, or one at Order Confirmed, can have an order
  const eligible = dealStatus === "WON" || (dealStatus === "OPEN" && stageKey === "ORDER");
  if (!canRead || loading) return null;
  if (orders.length === 0 && !(eligible && canCreate)) return null;

  const create = async () => {
    if (!po.po_number.trim()) return error("Validation Error", "Enter the buyer's PO number");
    setSaving(true);
    try {
      await api.post("/api/SalesOrder/Insert", { deal_id: dealId, po_number: po.po_number.trim(), po_date: po.po_date || undefined, notes: po.notes.trim() || undefined });
      success("Order made", "Dispatch against it from the Orders page");
      setOpen(false);
      setPo({ po_number: "", po_date: "", notes: "" });
      await load();
      onChanged();
    } catch (err: any) {
      error("Could not make the order", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          <PackageCheck className="size-4 text-primary" /> Sales order
        </p>
        {eligible && canCreate && live.length === 0 && (
          <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setOpen(true)}>
            Make order
          </Button>
        )}
      </div>
      {orders.length === 0 ? (
        <p className="text-xs text-muted-foreground">The buyer's PO is in. Make the order to track what is dispatched against it.</p>
      ) : (
        <ul className="divide-y divide-border">
          {orders.map((o) => (
            <li key={o.id} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Link to={`/orders/${o.id}`} className="text-sm font-semibold text-primary hover:underline">
                  {o.order_number}
                </Link>
                <p className="text-[11px] text-muted-foreground">
                  PO {o.po_number} · {formatINR(o.taxable_total)} before GST · {o.progress_pct || 0}% sent
                </p>
              </div>
              <OrderStatusBadge status={o.order_status as OrderStatus} />
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && !saving && setOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Make the sales order</DialogTitle>
            <DialogDescription>The lines, tolerance, packing and delivery dates come from the accepted quotation (or the deal's products).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Buyer's PO number *</Label>
                <Input value={po.po_number} onChange={(e) => setPo({ ...po, po_number: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>PO date</Label>
                <Input type="date" value={po.po_date} onChange={(e) => setPo({ ...po, po_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={po.notes} onChange={(e) => setPo({ ...po, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={create} disabled={saving || !po.po_number.trim()}>
              {saving ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Make order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
